import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, inject } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalInterceptor,
    MsalService,
} from '@azure/msal-angular';
import {
    IPublicClientApplication,
    InteractionType,
    PublicClientApplication,
} from '@azure/msal-browser';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { provideToastr } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { provideDates } from './core/common/date/date.provider';
import { provideErrors } from './core/errors/response.provider';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';

export function MSALInstanceFactory(): IPublicClientApplication {
    const instance = new PublicClientApplication({
        auth: {
            clientId: environment.azureAd.clientId,
            authority: `https://login.microsoftonline.com/${environment.azureAd.tenantId}`,
            redirectUri: environment.azureAd.redirectUri,
            postLogoutRedirectUri: environment.azureAd.postLogoutRedirectUri, // Define where to redirect after logout
        },
        cache: {
            cacheLocation: environment.azureAd.cacheLocation,
            storeAuthStateInCookie: true,
        },
    });

    instance.initialize(); // Ensure initialization before any other API calls

    return instance;
}

export function MSALGuardConfigFactory() {
    return {
        interactionType: InteractionType.Popup,
        authRequest: {
            scopes: environment.azureAd.scopes,
        },
    };
}

export function MSALInterceptorConfigFactory() {
    return {
        interactionType: InteractionType.Popup,
        protectedResourceMap: new Map([
            [environment.apiUrl, environment.azureAd.scopes],
            ['https://graph.microsoft.com/v1.0/me', ['User.Read']], // Add Graph API endpoint
        ]),
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),
        provideAnimations(),
        provideToastr({
            timeOut: 10000, // 5000 default
            positionClass: 'toast-top-right',
            preventDuplicates: true,
            progressBar: true,
        }),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    // {
                    //     id: 'tr',
                    //     label: 'Turkish',
                    // },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi: true,
        },

        // Fuse
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'modern',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-amber',
                themes: [
                    { id: 'theme-default', name: 'Default' },
                    { id: 'theme-brand', name: 'Brand' },
                    { id: 'theme-teal', name: 'Teal' },
                    { id: 'theme-rose', name: 'Rose' },
                    { id: 'theme-purple', name: 'Purple' },
                    { id: 'theme-amber', name: 'Amber' },
                ],
            },
        }),

        // General providers
        provideDates(),
        provideErrors(),

        //MSAL
        { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
        { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
    ],
};
